"use client"

import React, { useEffect, useMemo, useState } from "react"
import Image from 'next/image'
import { usePathname } from "next/navigation"
import { useI18n } from "@/context/i18n"

// Default icon (data URL) — uses the SVG you provided so pages that don't pass
// `iconUrl` still show the intended WhatsApp artwork.
const DEFAULT_ICON = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path fill="#fff" d="M3.516 3.516c4.686-4.686 12.284-4.686 16.97 0s4.686 12.283 0 16.97a12 12 0 0 1-13.754 2.299l-5.814.735a.392.392 0 0 1-.438-.44l.748-5.788A12 12 0 0 1 3.517 3.517z"/><path fill="#25D366" d="M7.126 20.56l.3.158a9.85 9.85 0 0 0 11.534-1.758c3.843-3.843 3.843-10.074 0-13.918s-10.075-3.843-13.918 0a9.85 9.85 0 0 0-1.747 11.554l.16.303-.51 3.942a.196.196 0 0 0 .219.22z"/><path fill="#fff" d="M13.67 13.876l-.933 1.164a9.84 9.84 0 0 1-3.497-3.495l1.166-.933a.79.79 0 0 0 .23-.94L9.561 6.96a.79.79 0 0 0-.924-.445l-2.023.524a.797.797 0 0 0-.588.88 11.754 11.754 0 0 0 10.005 10.005.797.797 0 0 0 .88-.587l.525-2.023a.79.79 0 0 0-.445-.923L14.6 13.327a.79.79 0 0 0-.94.55z"/></svg>');

type JoinChatProps = {
  telephone?: string // E.164 or digits, e.g. +5217122315859
  message?: string
  position?: "right" | "left"
  mobileOnly?: boolean
  buttonDelay?: number // seconds
  labelOpen?: string
  variant?: "default" | "link"
  pillText?: string
  iconUrl?: string
}

export default function JoinChat({
  telephone = "+5217122315859",
  message,
  position = "right",
  mobileOnly = false,
  buttonDelay = 3,
  labelOpen = "",
  variant = "default",
  pillText = "",
  iconUrl = DEFAULT_ICON,
}: JoinChatProps) {
  const { t, lang } = useI18n()

  // Localized defaults (allow props to override). Use memo so they update when `lang` changes.
  const computedLabelOpen = useMemo(() => {
    return labelOpen && labelOpen !== '' ? labelOpen : t('joinchat_label_open')
  }, [labelOpen, lang, t])

  const computedPillText = useMemo(() => {
    return pillText && pillText !== '' ? pillText : t('joinchat_pill_text')
  }, [pillText, lang, t])
  const [visible, setVisible] = useState(false)
  const [open, setOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(true)
  const pathname = usePathname()


  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Mostrar tras delay
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), Math.max(0, buttonDelay) * 1000)
    return () => clearTimeout(t)
  }, [buttonDelay])

  // Auto-ocultar tooltip tras unos segundos (usado en variante link)
  useEffect(() => {
    if (!visible) return
    const t = setTimeout(() => setShowTooltip(false), 4000)
    return () => clearTimeout(t)
  }, [visible])

  // Ocultar en desktop si solo móvil
  const hiddenForDevice = useMemo(() => {
    if (!mobileOnly) return false
    if (typeof window === "undefined") return false
    return window.innerWidth >= 1024
  }, [mobileOnly])

  // Mensaje contextual según la ruta si no se pasa uno explícito
  const contextualMessage = useMemo(() => {
    // Normaliza: minúsculas, sin locale inicial (/es, /en, /es-MX), sin slash final.
    const raw = (pathname || "/").toLowerCase()
    let path = raw
      // elimina prefijo de locale de 2 letras o 2-5 con guion (es, en, es-mx)
      .replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, "")
    if (path === "") path = "/"
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1)

  const url = typeof window !== "undefined" ? window.location.href : undefined
  const title = typeof document !== "undefined" ? document.title : undefined

    // Service-specific: `/servicios/{slug}` -> joinchat_service_{slug}_message
    if (path === "/servicios" || path.startsWith("/servicios/")) {
      const parts = path.split('/').filter(Boolean)
      if (parts[0] === 'servicios' && parts[1]) {
        const slug = parts[1]
        const msgKey = `joinchat_service_${slug}_message`
        const msg = t(msgKey)
        if (msg !== msgKey) return msg
      }
      // fallback for servicios root
      return t('joinchat_root_message')
    }

    // Reglas específicas primero
    const regexRules: Array<[RegExp, string]> = [
      [/^\/categoria\/(study|estudio)/, t('joinchat_category_study_message')],
      [/^\/categoria\/(travel|viaje)/, t('joinchat_category_travel_message')],
      [/^\/categoria\/(participaciones)/, t('joinchat_promotions_message')],
      [/^\/servicios\/(lets-study|study)/, t('joinchat_category_study_message')],
      [/^\/servicios\/(lets-travel|travel)/, t('joinchat_category_travel_message')],
      [/^\/blog(\/|$)/, t('joinchat_root_message')],
      [/^\/seccion(\/|$)/, t('joinchat_root_message')],
      [/^\/tipo-cambio(\/|$)/, t('joinchat_tipo_cambio_message')],
    ]
    for (const [re, msg] of regexRules) {
      if (re.test(path)) return msg
    }

    // Reglas por prefijo (no colocar '/' aquí como prefijo genérico)
    const prefixRules: Array<[string, string]> = [
      ["/nosotros", t('joinchat_root_message')],
      ["/promociones", t('joinchat_promotions_message')],
      ["/bolsa_trabajo", t('joinchat_root_message')],
      ["/podcast", t('joinchat_podcast_message')],
      ["/divisas", t('joinchat_root_message')],
  ["/tipo-cambio", t('joinchat_tipo_cambio_message')],
      ["/categoria", t('joinchat_root_message')],
      ["/servicios", t('joinchat_root_message')],
    ]
    for (const [prefix, msg] of prefixRules) {
      if (path === prefix || path.startsWith(prefix + "/")) return msg
    }

    // Fallback raíz exacta
    if (path === "/") {
      return t('joinchat_root_message')
    }

    // Fallback genérico con título/URL
    const tail = url ? `: ${url}` : (title ? ` (${title})` : ".")
    // Fallback generic message constructed from root message + tail
    return `${t('joinchat_root_message')}${tail}`
  }, [pathname, lang, t])

  const phoneDigits = useMemo(() => telephone.replace(/[^\d]/g, ""), [telephone])
  const finalMessage = message && message.trim().length > 0 ? message : contextualMessage
  const encodedMsg = useMemo(() => encodeURIComponent(finalMessage), [finalMessage])
  const waHref = useMemo(() => `https://wa.me/${phoneDigits}?text=${encodedMsg}`, [phoneDigits, encodedMsg])

  if (!visible || hiddenForDevice) return null

  // Variante tipo enlace: sin chatbox, con pill fija
  if (variant === "link") {
    return (
      <div className={`fixed ${position === "left" ? "left-5" : "right-5"} bottom-5 z-50 select-none`}>
        <a
          href={waHref.replace('https://wa.me/', 'https://api.whatsapp.com/send?phone=').replace('?text=', '&text=')}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block h-14 w-14 rounded-full bg-[#25D366] shadow-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#25D366] transition-transform duration-200 hover:scale-105 active:scale-95"
          aria-label={computedLabelOpen}
          data-ga-action="whatsapp_open"
          data-ga-category="contact"
          data-ga-label="floating_link_variant"
          style={{ animation: "joinchat-pop 480ms cubic-bezier(.22,1,.36,1) both, joinchat-breathe 3800ms ease-in-out 520ms infinite", willChange: 'transform' } as any}
        >
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full" style={{ animation: "joinchat-pulse 1500ms ease-out infinite", willChange: 'box-shadow' }} />
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full" style={{ animation: "joinchat-pulse 1500ms ease-out infinite", animationDelay: '450ms' as any, willChange: 'box-shadow' }} />
          <span className="absolute inset-0 rounded-full flex items-center justify-center">
            {iconUrl ? (
              <Image src={iconUrl} alt="WhatsApp" width={28} height={28} className="h-7 w-7 object-contain" />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.516 3.516c4.686-4.686 12.284-4.686 16.97 0s4.686 12.283 0 16.97a12 12 0 0 1-13.754 2.299l-5.814.735a.392.392 0 0 1-.438-.44l.748-5.788A12 12 0 0 1 3.517 3.517z" fill="#fff"/>
                <path d="M7.126 20.56l.3.158a9.85 9.85 0 0 0 11.534-1.758c3.843-3.843 3.843-10.074 0-13.918s-10.075-3.843-13.918 0a9.85 9.85 0 0 0-1.747 11.554l.16.303-.51 3.942a.196.196 0 0 0 .219.22z" fill="#25D366"/>
                <path d="M13.67 13.876l-.933 1.164a9.84 9.84 0 0 1-3.497-3.495l1.166-.933a.79.79 0 0 0 .23-.94L9.561 6.96a.79.79 0 0 0-.924-.445l-2.023.524a.797.797 0 0 0-.588.88 11.754 11.754 0 0 0 10.005 10.005.797.797 0 0 0 .88-.587l.525-2.023a.79.79 0 0 0-.445-.923L14.6 13.327a.79.79 0 0 0-.94.55z" fill="#fff"/>
              </svg>
            )}
          </span>
        </a>
        {/* Tooltip pill solo desktop */}
        {showTooltip && (
          <div className={`hidden md:block absolute ${position === 'right' ? 'right-16' : 'left-16'} bottom-2`}>
            <div className="bg-white text-black text-sm px-3 py-1 rounded-full shadow-md whitespace-nowrap" style={{ animation: `${position === 'right' ? 'joinchat-pill-in-right' : 'joinchat-pill-in-left'} 420ms ease-out both` }}>
              {computedPillText}
            </div>
          </div>
        )}
        <style jsx global>{`
          @keyframes joinchat-pill-in-right { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
          @keyframes joinchat-pill-in-left { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        `}</style>
      </div>
    )
  }

  // Variante default con el markup/CSS proporcionado
  return (
    <div className={`joinchat joinchat--show ${position === 'left' ? 'joinchat--left' : ''} ${open ? 'joinchat--chatbox' : ''}`}>
      {/* Botón flotante (estilo anterior) */}
      {!open && (
        <button
          type="button"
          className="jc-custom-btn"
          aria-label={computedLabelOpen}
          onClick={() => { setOpen(true); setShowTooltip(false); }}
          data-ga-action="whatsapp_open"
          data-ga-category="contact"
          data-ga-label="floating_chatbox_open"
        >
          <span aria-hidden className="jc-pulse jc-pulse-1" />
          <span aria-hidden className="jc-pulse jc-pulse-2" />
          <span className="jc-ico" aria-hidden>
            {iconUrl ? (
              <Image src={iconUrl} alt="WhatsApp" width={28} height={28} className="h-7 w-7 object-contain" />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.516 3.516c4.686-4.686 12.284-4.686 16.97 0s4.686 12.283 0 16.97a12 12 0 0 1-13.754 2.299l-5.814.735a.392.392 0 0 1-.438-.44l.748-5.788A12 12 0 0 1 3.517 3.517z" fill="#fff"/>
                <path d="M7.126 20.56l.3.158a9.85 9.85 0 0 0 11.534-1.758c3.843-3.843 3.843-10.074 0-13.918s-10.075-3.843-13.918 0a9.85 9.85 0 0 0-1.747 11.554l.16.303-.51 3.942a.196.196 0 0 0 .219.22z" fill="#25D366"/>
                <path d="M13.67 13.876l-.933 1.164a9.84 9.84 0 0 1-3.497-3.495l1.166-.933a.79.79 0 0 0 .23-.94L9.561 6.96a.79.79 0 0 0-.924-.445l-2.023.524a.797.797 0 0 0-.588.88 11.754 11.754 0 0 0 10.005 10.005.797.797 0 0 0 .88-.587l.525-2.023a.79.79 0 0 0-.445-.923L14.6 13.327a.79.79 0 0 0-.94.55z" fill="#fff"/>
              </svg>
            )}
          </span>
        </button>
      )}

      {/* Chatbox */}
      {open && (
        <div className="joinchat__chatbox" role="dialog" aria-labelledby="joinchat__label" aria-modal="true">
          <div className="joinchat__header">
            <div id="joinchat__label">
              {iconUrl ? (
                <Image src={iconUrl} alt={t('whatsapp_title')} width={28} height={28} className="h-7 w-auto" />
              ) : (
              <svg className="joinchat__wa" width="120" height="28" viewBox="0 0 120 28" aria-hidden>
                <title>{t('whatsapp_title')}</title>
                <path d="M117.2 17c0 .4-.2.7-.4 1-.1.3-.4.5-.7.7l-1 .2c-.5 0-.9 0-1.2-.2l-.7-.7a3 3 0 0 1-.4-1 5.4 5.4 0 0 1 0-2.3c0-.4.2-.7.4-1l.7-.7a2 2 0 0 1 1.1-.3 2 2 0 0 1 1.8 1l.4 1a5.3 5.3 0 0 1 0 2.3m2.5-3c-.1-.7-.4-1.3-.8-1.7a4 4 0 0 0-1.3-1.2c-.6-.3-1.3-.4-2-.4-.6 0-1.2.1-1.7.4a3 3 0 0 0-1.2 1.1V11H110v13h2.7v-4.5c.4.4.8.8 1.3 1 .5.3 1 .4 1.6.4a4 4 0 0 0 3.2-1.5c.4-.5.7-1 .8-1.6.2-.6.3-1.2.3-1.9s0-1.3-.3-2zm-13.1 3c0 .4-.2.7-.4 1l-.7.7-1.1.2c-.4 0-.8 0-1-.2-.4-.2-.6-.4-.8-.7a3 3 0 0 1-.4-1 5.4 5.4 0 0 1 0-2.3c0-.4.2-.7.4-1 .1-.3.4-.5.7-.7a2 2 0 0 1 1-.3 2 2 0 0 1 1.9 1l.4 1a5.4 5.4 0 0 1 0 2.3m1.7-4.7a4 4 0 0 0-3.3-1.6c-.6 0-1.2.1-1.7.4a3 3 0 0 0-1.2 1.1V11h-2.6v13h2.7v-4.5c.3.4.7.8 1.2 1 .6.3 1.1.4 1.7.4a4 4 0 0 0 3.2-1.5c.4-.5.6-1 .8-1.6s.3-1.2.3-1.9-.1-1.3-.3-2c-.2-.6-.4-1.2-.8-1.6m-17.5 3.2 1.7-5 1.7 5zm.2-8.2-5 13.4h3l1-3h5l1 3h3L94 7.3zm-5.3 9.1-.6-.8-1-.5a11.6 11.6 0 0 0-2.3-.5l-1-.3a2 2 0 0 1-.6-.3.7.7 0 0 1-.3-.6c0-.2 0-.4.2-.5l.3-.3h.5l.5-.1c.5 0 .9 0 1.2.3.4.1.6.5.6 1h2.5c0-.6-.2-1.1-.4-1.5a3 3 0 0 0-1-1 4 4 0 0 0-1.3-.5 7.7 7.7 0 0 0-3 0c-.6.1-1 .3-1.4.5l-1 1a3 3 0 0 0-.4 1.5 2 2 0 0 0 1 1.8l1 .5 1.1.3 2.2.6c.6.2.8.5.8 1l-.1.5-.4.4a2 2 0 0 1-.6.2 2.8 2.8 0 0 1-1.4 0 2 2 0 0 1-.6-.3l-.5-.5-.2-.8H77c0 .7.2 1.2.5 1.6.2.5.6.8 1 1 .4.3.9.5 1.4.6a8 8 0 0 0 3.3 0c.5 0 1-.2 1.4-.5a3 3 0 0 0 1-1c.3-.5.4-1 .4-1.6 0-.5 0-.9-.3-1.2M74.7 8h-2.6v3h-1.7v1.7h1.7v5.8c0 .5 0 .9.2 1.2l.7.7 1 .3a7.8 7.8 0 0 0 2 0h.7v-2.1a3.4 3.4 0 0 1-.8 0l-1-.1-.2-1v-4.8h2V11h-2zm-7.6 9v.5l-.3.8-.7.6c-.2.2-.7.2-1.2.2h-.6l-.5-.2a1 1 0 0 1-.4-.4l-.1-.6.1-.6.4-.4.5-.3a4.8 4.8 0 0 1 1.2-.2 8 8 0 0 0 1.2-.2l.4-.3v1zm2.6 1.5v-5c0-.6 0-1.1-.3-1.5l-1-.8-1.4-.4a10.9 10.9 0 0 0-3.1 0l-1.5.6c-.4.2-.7.6-1 1a3 3 0 0 0-.5 1.5h2.7c0-.5.2-.9.5-1a2 2 0 0 1 1.3-.4h.6l.6.2.3.4.2.7c0 .3 0 .5-.3.6-.1.2-.4.3-.7.4l-1 .1a22 22 0 0 0-2.4.4l-1 .5c-.3.2-.6.5-.8.9-.2.3-.3.8-.3 1.3s.1 1 .3 1.3c.1.4.4.7.7 1l1 .4c.4.2.9.2 1.3.2a6 6 0 0 0 1.8-.2c.6-.2 1-.5 1.5-1a4 4 0 0 0 .2 1H70l-.3-1zm-11-6.7c-.2-.4-.6-.6-1-.8-.5-.2-1-.3-1.8-.3-.5 0-1 .1-1.5.4a3 3 0 0 0-1.3 1.2v-5h-2.7v13.4H53v-5.1c0-1 .2-1.7.5-2.2.3-.4.9-.6 1.6-.6.6 0 1 .2 1.3.6s.4 1 .4 1.8v5.5h2.7v-6c0-.6 0-1.2-.2-1.6 0-.5-.3-1-.5-1.3zm-14 4.7-2.3-9.2h-2.8l-2.3 9-2.2-9h-3l3.6 13.4h3l2.2-9.2 2.3 9.2h3l3.6-13.4h-3zm-24.5.2L18 15.6c-.3-.1-.6-.2-.8.2A20 20 0 0 1 16 17c-.2.2-.4.3-.7.1-.4-.2-1.5-.5-2.8-1.7-1-1-1.7-2-2-2.4-.1-.4 0-.5.2-.7l.5-.6.4-.6v-.6L10.4 8c-.3-.6-.6-.5-.8-.6H9c-.2 0-.6.1-.9.5C7.8 8.2 7 9 7 10.7s1.3 3.4 1.4 3.6c.2.3 2.5 3.7 6 5.2l1.9.8c.8.2 1.6.2 2.2.1s2-.8 2.3-1.6c.3-.9.3-1.5.2-1.7l-.7-.4zM14 25.3c-2 0-4-.5-5.8-1.6l-.4-.2-4.4 1.1 1.2-4.2-.3-.5A11.5 11.5 0 0 1 22.1 5.7 11.5 11.5 0 0 1 14 25.3M14 0A13.8 13.8 0 0 0 2 20.7L0 28l7.3-2A13.8 13.8 0 1 0 14 0"/>
              </svg>
              )}
            </div>
            <div className="joinchat__close" role="button" tabIndex={0} aria-label={t('joinchat_close')} onClick={() => setOpen(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(false) }} />
          </div>
          <div className="joinchat__scroll">
            <div className="joinchat__content">
              <div className="joinchat__chat">
                <div className="joinchat__bubble">{finalMessage}</div>
              </div>
              <a
                className="joinchat__open"
                role="button"
                tabIndex={0}
                href={waHref.replace('https://wa.me/', 'https://api.whatsapp.com/send?phone=').replace('?text=', '&text=')}
                target="_blank"
                rel="noopener noreferrer"
                data-ga-action="whatsapp_send"
                data-ga-category="contact"
                data-ga-label="chatbox_send"
              >
                <div className="joinchat__open__text">{computedLabelOpen}</div>
                <svg className="joinchat__open__icon" width="60" height="60" viewBox="0 0 400 400" aria-hidden>
                  <path className="joinchat__pa" d="M168.83 200.504H79.218L33.04 44.284a1 1 0 0 1 1.386-1.188L365.083 199.04a1 1 0 0 1 .003 1.808L34.432 357.903a1 1 0 0 1-1.388-1.187l29.42-99.427"></path>
                  <path className="joinchat__pb" d="M318.087 318.087c-52.982 52.982-132.708 62.922-195.725 29.82l-80.449 10.18 10.358-80.112C18.956 214.905 28.836 134.99 81.913 81.913c65.218-65.217 170.956-65.217 236.174 0 42.661 42.661 57.416 102.661 44.265 157.316"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
/* Botón personalizado (estilo anterior) */
.jc-custom-btn { position:absolute; bottom:8px; right:8px; height:var(--s); width:var(--s); border-radius:50%; background:#25D366; color:#fff; box-shadow: 1px 6px 24px rgba(7,94,84,.24); cursor:pointer; outline:none; border:0; display:inline-flex; align-items:center; justify-content:center; will-change: transform; animation: jc-pop 480ms cubic-bezier(.22,1,.36,1) both, jc-breathe 3800ms ease-in-out 520ms infinite }
.joinchat--left .jc-custom-btn { right:auto; left:8px }
.jc-custom-btn:focus-visible { box-shadow: 0 0 0 3px rgba(37,211,102,.4) }
.jc-ico { position:relative; z-index:1; display:flex; align-items:center; justify-content:center }
.jc-pulse { position:absolute; inset:0; border-radius:50%; pointer-events:none; box-shadow: 0 0 0 0 rgba(37,211,102,.45); animation: jc-pulse 1500ms ease-out infinite; will-change: box-shadow }
.jc-pulse-2 { animation-delay: 450ms }

@keyframes jc-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes jc-pop { 0% { transform: scale(.6); opacity:0 } 100% { transform: scale(1); opacity:1 } }
@keyframes jc-pulse { 0% { box-shadow: 0 0 0 0 rgba(37,211,102,.45) } 70% { box-shadow: 0 0 0 14px rgba(37,211,102,0) } 100% { box-shadow: 0 0 0 0 rgba(37,211,102,0) } }

:root {
  --joinchat-ico: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M3.516 3.516c4.686-4.686 12.284-4.686 16.97 0s4.686 12.283 0 16.97a12 12 0 0 1-13.754 2.299l-5.814.735a.392.392 0 0 1-.438-.44l.748-5.788A12 12 0 0 1 3.517 3.517zm3.61 17.043.3.158a9.85 9.85 0 0 0 11.534-1.758c3.843-3.843 3.843-10.074 0-13.918s-10.075-3.843-13.918 0a9.85 9.85 0 0 0-1.747 11.554l.16.303-.51 3.942a.196.196 0 0 0 .219.22z'/%3E%3C/svg%3E");
  --joinchat-font: -apple-system,blinkmacsystemfont,"Segoe UI",roboto,oxygen-sans,ubuntu,cantarell,"Helvetica Neue",sans-serif
}

.joinchat {
  --sep: 20px;
  --bottom: var(--sep);
  --s: 60px;
  --h: calc(var(--s)/2);
  --header: max(50px,var(--s)*1.166667);
  --vh: 100vh;
  --ch: 142;
  --cs: 70%;
  --cl: 49%;
  --bw: 1;
  --text: hsl(0deg 0% clamp(0%,var(--bw)*1000%,100%)/clamp(70%,var(--bw)*1000%,100%));
  --hover: hsl(var(--ch) var(--cs) calc(var(--cl) + 20%));
  --hsl: var(--ch) var(--cs) var(--cl);
  --color: hsl(var(--hsl));
  --dark: hsl(var(--ch) var(--cs) calc(var(--cl) - 10%));
  --bg: hsl(var(--hsl)/4%);
  --msg: var(--color);
  color: var(--text);
  display: none;
  position: fixed;
  z-index: 9000;
  right: var(--sep);
  bottom: var(--bottom);
  font: normal normal normal 16px/1.625em var(--joinchat-font);
  letter-spacing: 0;
  animation: joinchat_show .5s cubic-bezier(.18,.89,.32,1.28) 10ms both;
  transform: scale3d(0,0,0);
  transform-origin: calc(var(--s)/-2) calc(var(--s)/-4);
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  touch-action: manipulation;
  -webkit-font-smoothing: antialiased;
  -webkit-tap-highlight-color: rgb(0 0 0/0)
}

.joinchat *,.joinchat :after,.joinchat :before { box-sizing: border-box }

.joinchat--show { display: block; transform: scaleX(1) }
.joinchat--left { right: auto; left: var(--sep) }
.joinchat--dark { --msg: var(--dark) }
.joinchat--noanim { animation: none }
.joinchat--chatbox:not(.joinchat--noanim) { transform-origin: 0 0; animation-timing-function: ease-in-out }
.joinchat--hidden { display: none!important }

@supports not (width: clamp(1px,1%,10px)) {
  .joinchat { --text:hsl(0deg 0% calc(var(--bw)*1%)/90%) }
}

.joinchat__button {
  position: absolute; z-index: 2; bottom: 8px; right: 8px;
  height: var(--s); width: var(--s);
  background: #25d366 var(--joinchat-ico) 50%/60% no-repeat; color: inherit; border-radius: 50%;
  box-shadow: 1px 6px 24px 0 rgba(7,94,84,.24); cursor: pointer; transition: background-color .2s linear
}
.joinchat__button:hover { background-color: #128c7e }
.joinchat:has(.joinchat__chatbox) .joinchat__button:hover { transition-duration: 1.5s }
.joinchat__button:active { background-color: #128c7e; transition: none }
.joinchat--left .joinchat__button { right: auto; left: 8px }
.joinchat--chatbox .joinchat__button { display: none }

.joinchat__tooltip { position: absolute; top: calc(var(--h) - 16px); right: calc(var(--s) + 16px); max-width: calc(100vw - var(--s) - 45px); height: 32px; padding: 0 14px; border-radius: 16px; background: #fff; color: rgba(0,0,0,.8); line-height: 31px; white-space: nowrap; opacity: 0; transition: opacity .3s ease-out .4s; filter: drop-shadow(0 1px 4px rgba(0,0,0,.4)); pointer-events: none }
.joinchat__tooltip:after { content: ""; position: absolute; top: 10px; right: -6px; border: 8px solid transparent; border-width: 6px 0 6px 8px; border-left-color: #fff }
.joinchat--left .joinchat__tooltip { right: auto; left: calc(var(--s) + 16px) }
.joinchat--left .joinchat__tooltip:after { left: -6px; right: auto; border-color: transparent; border-width: 6px 8px 6px 0; border-right-color: #fff }
.joinchat:has(.joinchat__button:hover) .joinchat__tooltip { opacity: 1; animation: none; transition: opacity .2s }

.joinchat__chatbox { content-visibility: hidden; display: flex; flex-direction: column; position: absolute; bottom: 0; right: 0; z-index: 1; width: calc(100vw - var(--sep)*2); max-width: 400px; max-height: calc(var(--vh) - var(--bottom) - var(--sep)); border-radius: calc(var(--h) + 2px); background: #fff linear-gradient(180deg,var(--color),var(--color) var(--header),var(--bg) var(--header),var(--bg)); box-shadow: 0 2px 6px 0 rgba(0,0,0,.5); text-align: left; overflow: hidden; transform: scale3d(0,0,0); opacity: 0; transition: max-height .2s ease-out,opacity .4s ease-out,transform 0s linear .3s }
.joinchat--chatbox .joinchat__chatbox { content-visibility: visible; opacity: 1; transform: scaleX(1); transition: max-height .2s ease-out,opacity .2s ease-out,transform 0s linear }
.joinchat--left .joinchat__chatbox { right: auto; left: 0 }

.joinchat__header { display: flex; flex-flow: row; align-items: center; position: relative; flex-shrink: 0; height: var(--header); padding: 0 70px 0 26px }
.joinchat__wa { display: block; fill: currentcolor; opacity: .8 }
.joinchat__close { --size: 34px; position: absolute; top: calc(50% - var(--size)/2); right: 24px; width: var(--size); height: var(--size); border-radius: 50%; background: rgba(0,0,0,.4) url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M24 2.4 21.6 0 12 9.6 2.4 0 0 2.4 9.6 12 0 21.6 2.4 24l9.6-9.6 9.6 9.6 2.4-2.4-9.6-9.6z'/%3E%3C/svg%3E") 50%/12px no-repeat; cursor: pointer; transition: background-color .3s ease-out }
.joinchat__close:hover { background-color: #dc2626 }
.joinchat__close:active { background-color: #b91c1c }

.joinchat__scroll { overflow: hidden scroll; -webkit-overflow-scrolling: touch }
.joinchat__content { display: flex; flex-direction: column; width: calc(100% + 4px); padding: 20px 0 8px }
.joinchat__content:after { content: ""; position: absolute; bottom: 0; left: 0; right: 4px; height: calc(var(--s) + 20px); background: #fff linear-gradient(180deg,var(--bg),var(--bg)); -webkit-mask-image: linear-gradient(rgb(0 0 0/0),rgb(0 0 0/60%) 25%,rgb(0 0 0/92%) 70%,rgb(0 0 0/100%) 95%,rgb(0 0 0/100%)); mask-image: linear-gradient(rgb(0 0 0/0),rgb(0 0 0/60%) 25%,rgb(0 0 0/92%) 70%,rgb(0 0 0/100%) 95%,rgb(0 0 0/100%)); pointer-events: none }

.joinchat__chat { --peak_l: path("M17 25V0C17 12.877 6.082 14.9 1.031 15.91c-1.559.31-1.179 2.272.004 2.272C9.609 18.182 17 18.088 17 25z"); --peak_r: path("M0 25.68V0c0 13.23 10.92 15.3 15.97 16.34 1.56.32 1.18 2.34 0 2.34-8.58 0-15.97-.1-15.97 7Z"); --peak: var(--peak_l); display: flex; flex-direction: column; align-items: flex-start; padding-bottom: 10px }
.joinchat__bubble { position: relative; min-width: 60px; max-width: calc(100% - 52px); min-height: 56px; padding: 15px 20px; margin: 0 26px 16px; border-radius: 26px; background: #fff; color: #4a4a4a; word-break: break-word; filter: drop-shadow(0 1px 2px rgba(0,0,0,.3)); transform-origin: 0 50%; animation: joinchat_show .25s cubic-bezier(0,0,.58,1.19) 10ms both }
.joinchat__bubble:before { content: ""; position: absolute; bottom: 18px; left: -15px; width: 17px; height: 25px; background: inherit; clip-path: var(--peak) }

.joinchat__open { position: sticky; z-index: 2; bottom: 8px; align-self: flex-end; display: flex; flex-direction: row; min-width: var(--s); height: var(--s); margin: 0 8px; background: var(--color); color: inherit; border-radius: var(--h); box-shadow: 0 1px 2px 0 rgba(0,0,0,.3); overflow: hidden; cursor: pointer; transition: background-color .2s linear; text-decoration: none }
.joinchat__open:hover { background: var(--hover) }
.joinchat--left .joinchat__open { align-self: flex-start; flex-direction: row-reverse }
.joinchat__open__icon { flex-shrink: 0; width: var(--s); height: var(--s); max-width: var(--s); padding: calc(var(--s)*0.18); margin: 0; overflow: hidden }
.joinchat__open__icon path { fill: none!important; stroke: var(--text)!important; stroke-linecap: round; stroke-width: 33px }
.joinchat--chatbox .joinchat__open__icon .joinchat__pa { --stroke: 1097; stroke-dasharray: var(--stroke); stroke-dashoffset: var(--stroke); animation: joinchat_send_svg 6s .2s ease-in-out infinite }
.joinchat--chatbox .joinchat__open__icon .joinchat__pb { --stroke: 1020; stroke-dasharray: var(--stroke); stroke-dashoffset: var(--stroke); animation: joinchat_send_svg 6s 3.2s ease-in-out infinite }
.joinchat__open__text { max-width: min(400px - 16px - var(--s),100vw - 16px - var(--s) - 2*var(--sep)); padding: 0 4px 0 24px; font-weight: 600; line-height: var(--s); white-space: nowrap }
.joinchat--left .joinchat__open__text { padding: 0 24px 0 4px }

@keyframes joinchat_show { 0% { transform: scale3d(0,0,0) } to { transform: scaleX(1) } }
@keyframes joinchat_send_svg { 0%,50%,to { stroke-dashoffset: var(--stroke) } 5%,45% { stroke-dashoffset: 0 } }

@media (orientation: landscape) and (height <= 480px),(width <= 480px) {
  .joinchat { --sep:6px; --header: max(50px,var(--s)*0.9166667) }
  .joinchat__bubble { max-width: calc(100% - 40px); padding: 16px; margin: 0 20px 10px; line-height: 24px }
}

@media (hover: hover) {
  .joinchat:has(.joinchat__open:hover) .joinchat__qr { display: flex }
}

@media (prefers-reduced-motion) {
  .joinchat,.joinchat__open__icon path { animation: none }
  .joinchat__open__text { transition: none!important }
  .joinchat__bubble { animation: none!important }
}
      `}</style>
    </div>
  )
}
